import { useTranslation } from "react-i18next";
import { useRef, type KeyboardEvent } from "react";

import { cn } from "@/utils/cn";

import type { SearchFilter, SearchFilterId } from "../types";

interface SearchFilterChipsProps {
  filters: SearchFilter[];
  active: SearchFilterId;
  /** `viaPointer` is true for a mouse click, so the caller can refocus the input. */
  onChange: (id: SearchFilterId, viaPointer: boolean) => void;
}

/**
 * Single-select scope chips shown between the input and the result list. Acts
 * as a WAI-ARIA radiogroup with roving tabindex: only the active chip is a tab
 * stop; ←/→/Home/End move (and select) between chips. Arrow/Enter/Space are
 * stopped from bubbling so cmdk's list keyboard handler never sees them.
 */
export const SearchFilterChips = ({ filters, active, onChange }: SearchFilterChipsProps) => {
  const { t } = useTranslation(["common"]);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Only "All" → nothing meaningful to scope by.
  if (filters.length <= 1) return null;

  const selectAt = (i: number) => {
    const next = filters[i];
    onChange(next.id, false);
    btnRefs.current[i]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowLeft": {
        e.preventDefault();
        e.stopPropagation();
        const dir = e.key === "ArrowRight" ? 1 : -1;
        selectAt((i + dir + filters.length) % filters.length);
        break;
      }
      case "Home":
        e.preventDefault();
        e.stopPropagation();
        selectAt(0);
        break;
      case "End":
        e.preventDefault();
        e.stopPropagation();
        selectAt(filters.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        e.stopPropagation();
        onChange(filters[i].id, false);
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={t("common:a11y.filter_results_by_scope")}
      className="flex items-center gap-1.5 overflow-x-auto border-b border-border px-3 py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {filters.map((filter, i) => {
        const isActive = filter.id === active;
        return (
          <button
            key={filter.id}
            ref={(el) => {
              btnRefs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={(e) => onChange(filter.id, e.detail > 0)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors outline-none",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
              isActive
                ? "border-primary bg-primary-muted text-primary"
                : "border-border text-muted-foreground hover:border-border-strong hover:text-foreground",
            )}
          >
            {filter.icon && (
              <span className="flex size-3.5 shrink-0 items-center justify-center [&_svg]:size-3.5">
                {filter.icon}
              </span>
            )}
            {filter.label}
          </button>
        );
      })}
    </div>
  );
};
